import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ open, images = [], index = 0, title, client, onClose, onIndexChange }) {
  const count = images.length;

  const next = useCallback(() => onIndexChange((index + 1) % count), [index, count, onIndexChange]);
  const prev = useCallback(() => onIndexChange((index - 1 + count) % count), [index, count, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
          onClick={onClose}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 md:px-12 py-5 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0">
              {client && (
                <p className="font-mono-tech text-[10px] uppercase tracking-widest text-[hsl(var(--signal))] mb-1">{client}</p>
              )}
              <p className="font-display font-medium text-lg md:text-xl tracking-tight truncate">{title}</p>
            </div>
            <div className="flex items-center gap-5 shrink-0 pl-4">
              <span className="font-mono-tech text-sm text-white/60">
                {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
              </span>
              <button data-testid="lightbox-close" onClick={onClose} aria-label="Close" className="text-white/70 hover:text-white transition-colors">
                <X className="h-7 w-7" />
              </button>
            </div>
          </div>

          {/* Image stage — clicks on the padding bubble to root (close); image & buttons stop propagation */}
          <div className="relative flex-1 flex items-center justify-center px-4 md:px-20 pb-4 min-h-0">
            {count > 1 && (
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous"
                className="absolute left-3 md:left-6 grid place-items-center h-11 w-11 md:h-14 md:w-14 bg-black/50 border border-white/40 text-white hover:bg-white hover:text-black transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            <AnimatePresence mode="wait">
              <motion.img
                key={index}
                src={images[index]}
                alt={`${title} — ${index + 1}`}
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="max-h-full max-w-full object-contain select-none"
              />
            </AnimatePresence>

            {count > 1 && (
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next"
                className="absolute right-3 md:right-6 grid place-items-center h-11 w-11 md:h-14 md:w-14 bg-black/50 border border-white/40 text-white hover:bg-white hover:text-black transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Thumbnails */}
          {count > 1 && (
            <div className="flex items-center justify-center gap-2 px-6 pb-6 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
              {images.map((src, i) => (
                <button
                  key={src}
                  data-testid={`lightbox-thumb-${i}`}
                  onClick={() => onIndexChange(i)}
                  className={`h-14 w-20 shrink-0 overflow-hidden border transition-all ${
                    i === index ? "border-[hsl(var(--signal))] opacity-100" : "border-white/20 opacity-50 hover:opacity-90"
                  }`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
